import React from 'react';
import styles from './BankingOverview.module.scss';
import { infoIconGreen, infoIconYellow, infoToolTip } from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { formatDate } from '../../../../../../utils/utils';
import { ALL_ACCOUNTS } from '../../../../../../utils/constants/commonConstants';
import MissingDaysTooltipContent, {
    TooltipDateRange,
    dedupeRanges,
    mergeAdjacentRanges,
} from '../MissingDaysTooltipContent';
import MismatchPill from '../MismatchPill';

// TypeScript interfaces for component props
interface PeriodWiseMissingDate {
    period: string;
    missingDates: TooltipDateRange[];
    balanceMismatchRanges?: TooltipDateRange[];
}

interface AccountData {
    accountId: string;
    accountNumber: string;
    name: string;
    closingBalance: number;
    closingBalanceDate: string;
    odSanctionLimit: number;
    transactionsLTM: number;
    missingDays: number;
    periodWiseMissingDates?: PeriodWiseMissingDate[];
}

interface SummaryAccountData {
    accountNumber: string;
    bankName: string;
}

interface BankingOverviewProps {
    data: AccountData[];
    summaryData: SummaryAccountData[];
    onAccountSelect: (accountId: string) => void;
}

const BankingOverview = ({ data, summaryData, onAccountSelect }: BankingOverviewProps) => {
    const formatNumber = (num: number | undefined | null): string => {
        if (typeof num !== 'number') return '-';
        return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
    };

    const formatClosingBalance = (num: number | undefined | null): { value: string; isNegative: boolean } => {
        if (typeof num !== 'number') return { value: '-', isNegative: false };
        const isNegative = num < 0;
        const formattedNum = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(num));
        return {
            value: isNegative ? `(${formattedNum})` : formattedNum,
            isNegative,
        };
    };

    const formatBankName = (account: AccountData): string => {
        if (account.name === ALL_ACCOUNTS) {
            return account.name ?? '-';
        }

        return `${account.name ? account.name : '-'}`;
    };

    const formatMissingDatesForTooltip = (periodWiseMissingDates: PeriodWiseMissingDate[]): JSX.Element => {
        if (!periodWiseMissingDates || periodWiseMissingDates.length === 0) {
            return <div>All data present</div>;
        }

        // The backend splits missing-date ranges at month boundaries — merge them back so a
        // range like May 25 – Jun 5 reads as one range. Balance-mismatch ranges arrive whole
        // but repeated under every month they touch, so only dedupe those.
        const missingRanges = mergeAdjacentRanges(periodWiseMissingDates.flatMap((period) => period.missingDates || []));
        const balanceMismatchRanges = dedupeRanges(periodWiseMissingDates.flatMap((period) => period.balanceMismatchRanges || []));

        return <MissingDaysTooltipContent missingRanges={missingRanges} balanceMismatchRanges={balanceMismatchRanges} />;
    };

    const renderMissingDaysCell = (account: AccountData): JSX.Element | string => {
        if (account.name === ALL_ACCOUNTS) {
            return '';
        }

        const hasMismatch =
            account.periodWiseMissingDates?.some((period) => (period.balanceMismatchRanges?.length ?? 0) > 0) ?? false;
        // Mismatch-only accounts (no plain missing days) must still open the tooltip.
        const hasTooltipData =
            !!account.periodWiseMissingDates && account.periodWiseMissingDates.length > 0 && (account.missingDays > 0 || hasMismatch);

        const daysChip = (
            <div
                style={
                    account.missingDays > 0
                        ? { padding: '0.25rem 0.5rem', borderRadius: '1rem', background: '#FEF9EC', color: '#E4AB01' }
                        : { padding: '0.25rem 0.5rem', borderRadius: '1rem', background: '#E1FFE1', color: '#49A71B' }
                }
                className={styles.missingDaysCell}
            >
                <span>{account.missingDays} days</span>
                <img src={account.missingDays > 0 ? infoIconYellow : infoIconGreen} alt={account.missingDays > 0 ? 'Warning' : 'OK'} />
            </div>
        );

        const cellContent = (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {daysChip}
                {hasMismatch && <MismatchPill />}
            </div>
        );

        if (hasTooltipData) {
            return (
                <div>
                    <Tooltip
                        content={formatMissingDatesForTooltip(account.periodWiseMissingDates || [])}
                        direction="left"
                        usePortal={true}
                        style={{
                            width: 'auto',
                            overflow: 'auto',
                            top: '8rem',
                            zIndex: 99999,
                            position: 'absolute',
                            marginTop: '4px',
                        }}
                    >
                        {cellContent}
                    </Tooltip>
                </div>
            );
        }

        return cellContent;
    };

    const renderHeaderWithTooltip = (title: string, tooltipContent: string): JSX.Element => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>{title}</span>
                <Tooltip
                    content={<div>{tooltipContent}</div>}
                    direction="left"
                    usePortal={true}
                    style={{
                        width: 'auto',
                        maxWidth: '350px',
                        overflow: 'auto',
                        zIndex: 99999,
                        position: 'absolute',
                        marginTop: '4px',
                        top: '2rem',
                    }}
                >
                    <img src={infoToolTip} alt="Info" style={{ display: 'block', width: '16px', height: '16px', cursor: 'pointer' }} />
                </Tooltip>
            </div>
        );
    };

    return (
        <div className={styles.bankingOverviewContainer}>
            <table className={styles.overviewTable}>
                <thead className={styles.TableHead}>
                    <tr className={styles.TableHeadRow}>
                        <th className={styles.TableHeadRowItem}>Bank Name</th>
                        <th className={styles.TableHeadRowItem}>Account No.</th>
                        <th className={styles.TableHeadRowItem}>Closing Balance</th>
                        <th className={styles.TableHeadRowItem}>Closing Bal. Date</th>
                        <th className={styles.TableHeadRowItem}>OD/CC Sanction Limit</th>
                        <th className={styles.TableHeadRowItem}>
                            {renderHeaderWithTooltip(
                                'Transactions (LTM)',
                                'Total transactions over the running last 12 months, updated on a rolling basis to reflect the most recent period.',
                            )}
                        </th>
                        <th className={styles.TableHeadRowItem}>
                            {renderHeaderWithTooltip(
                                'Missing Days (LTM)',
                                'Number of days with no data available in the running last 12 months.',
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody className={styles.TableBody}>
                    {data?.map((account) => (
                        <tr
                            key={account.accountId}
                            className={`${styles.TableBodyRow} ${account.name === ALL_ACCOUNTS ? styles.allAccountsRow : ''}`}
                            onClick={() => onAccountSelect(account.accountId)}
                        >
                            <td className={styles.TableBodyRowItem}>{formatBankName(account)}</td>
                            <td className={styles.TableBodyRowItem}>
                                {account.name === ALL_ACCOUNTS ? `${data?.length - 1} Accounts` : account.accountNumber}
                            </td>
                            <td
                                style={{
                                    fontWeight: '600',
                                    color: account.closingBalance < 0 ? '#F10707' : 'inherit',
                                    textAlign: 'right',
                                }}
                                className={styles.TableBodyRowItem}
                            >
                                {account.name === ALL_ACCOUNTS ? '' : formatClosingBalance(account.closingBalance).value}
                            </td>
                            <td className={styles.TableBodyRowItem}>
                                {account.name === ALL_ACCOUNTS ? '' : formatDate(account.closingBalanceDate)}
                            </td>
                            <td style={{ textAlign: 'right' }} className={styles.TableBodyRowItem}>
                                {formatNumber(account.odSanctionLimit)}
                            </td>
                            <td style={{ textAlign: 'right' }} className={styles.TableBodyRowItem}>
                                {formatNumber(account.transactionsLTM)}
                            </td>
                            <td className={styles.TableBodyRowItem}>{renderMissingDaysCell(account)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default BankingOverview;
